/**
 * Floating Navbar runtime
 * - Smart hide/persistent behavior
 * - Home icon actions (native drawers + links)
 * - Collection filter/sort + mirrored pagination
 * - Product add to bag / buy now actions
 */

(function() {
  'use strict';

  if (window.UntamedFloatingNavbarManager) return;

  const OVERLAY_BODY_CLASSES = [
    'drawer-open',
    'newsletter-popup-open',
    'cart-drawer-open',
    'wishlist-drawer-open',
    'menu-drawer-open',
    'search-drawer-open',
    'localization-drawer-open'
  ];

  class FloatingNavbarInstance {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.mode = root.dataset.mode || 'home';
      this.layoutMode = root.dataset.layoutMode || 'floating';
      this.scrollBehavior = root.dataset.scrollBehavior || 'smart_hide';
      this.enablePagination = root.dataset.enablePagination === 'true';
      this.lastScrollY = window.scrollY || 0;
      this.isHidden = false;
      this.destroyed = false;

      this.paginationWrap = root.querySelector('[data-floating-pagination]');
      this.paginationPages = root.querySelector('[data-pagination-pages]');
      this.paginationPrevBtn = root.querySelector('[data-pagination-prev]');
      this.paginationNextBtn = root.querySelector('[data-pagination-next]');
      this.collectionRoot = root.querySelector('[data-floating-navbar-collection]');
      this.productWishlistBtn = root.querySelector('[data-floating-wishlist-btn]');

      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBodyMutation = this.handleBodyMutation.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleIntersectionHide = this.handleIntersectionHide.bind(this);
      this.handleWishlistUpdated = this.handleWishlistUpdated.bind(this);

      this.intersectionObserver = null;
      this.bodyObserver = null;
      this._lastOverlayState = false;
    }

    init() {
      if (this.destroyed) return;

      this.root.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
      this.bindVisibilityEngine();
      this.initCollectionPagination();
      this.bindWishlistState();
      this.initIcons();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.root.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('wishlist:updated', this.handleWishlistUpdated);
      window.removeEventListener('storage', this.handleWishlistUpdated);

      if (this.bodyObserver) {
        this.bodyObserver.disconnect();
        this.bodyObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    initIcons() {
      if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
        window.UntamedIcons.refresh();
      }
    }

    bindVisibilityEngine() {
      if (this.scrollBehavior === 'smart_hide') {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      this.bodyObserver = new MutationObserver(this.handleBodyMutation);
      this.bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      this.setupProductBarOverlapGuard();
      this.applyVisibility();
    }

    setupProductBarOverlapGuard() {
      const productBar = document.querySelector('.product-floating-bar');
      if (!productBar) return;

      this.intersectionObserver = new MutationObserver(this.handleIntersectionHide);
      this.intersectionObserver.observe(productBar, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    handleIntersectionHide() {
      this.applyVisibility();
    }

    handleResize() {
      this.applyVisibility();
      if (this.mode === 'collection' && this.enablePagination) {
        this.initCollectionPagination();
      }
    }

    handleBodyMutation() {
      const currentOverlayState = this.isAnyOverlayOpen();
      if (currentOverlayState === this._lastOverlayState) return;
      this._lastOverlayState = currentOverlayState;
      this.applyVisibility();
    }

    isAnyOverlayOpen() {
      if (document.body.style.overflow === 'hidden') return true;

      for (let i = 0; i < OVERLAY_BODY_CLASSES.length; i += 1) {
        if (document.body.classList.contains(OVERLAY_BODY_CLASSES[i])) {
          return true;
        }
      }

      const productFloatingBarVisible = document.querySelector('.product-floating-bar.is-visible');
      if (productFloatingBarVisible) return true;

      return false;
    }

    applyVisibility() {
      if (this.isAnyOverlayOpen()) {
        this.hide();
        return;
      }

      if (this.scrollBehavior !== 'smart_hide') {
        this.show();
        return;
      }

      const currentY = window.scrollY || 0;
      if (currentY <= 10) {
        this.show();
      }
    }

    handleScroll() {
      if (this.scrollBehavior !== 'smart_hide') return;

      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._processScroll();
      });
    }

    _processScroll() {
      if (this.isAnyOverlayOpen()) {
        this.hide();
        return;
      }

      const currentY = window.scrollY || 0;
      const delta = currentY - this.lastScrollY;

      if (currentY <= 10) {
        this.show();
      } else if (delta > 20) {
        this.hide();
      } else if (delta < -20) {
        this.show();
      } else {
        return;
      }

      this.lastScrollY = currentY;
    }

    hide() {
      if (this.isHidden) return;
      this.isHidden = true;
      this.root.classList.add('is-hidden');
    }

    show() {
      if (!this.isHidden) return;
      this.isHidden = false;
      this.root.classList.remove('is-hidden');
    }

    handleClick(event) {
      const navActionEl = event.target.closest('[data-nav-action]');
      if (navActionEl && this.root.contains(navActionEl)) {
        this.handleNavAction(event, navActionEl);
        return;
      }

      const productActionEl = event.target.closest('[data-product-action]');
      if (productActionEl && this.root.contains(productActionEl)) {
        this.handleProductAction(event, productActionEl);
        return;
      }

      const prevBtn = event.target.closest('[data-pagination-prev]');
      if (prevBtn && this.root.contains(prevBtn)) {
        event.preventDefault();
        this.navigateTo(prevBtn.dataset.href);
        return;
      }

      const nextBtn = event.target.closest('[data-pagination-next]');
      if (nextBtn && this.root.contains(nextBtn)) {
        event.preventDefault();
        this.navigateTo(nextBtn.dataset.href);
        return;
      }

      const pagePill = event.target.closest('[data-pagination-url]');
      if (pagePill && this.root.contains(pagePill)) {
        event.preventDefault();
        this.navigateTo(pagePill.dataset.paginationUrl);
      }
    }

    handleNavAction(event, element) {
      const action = element.dataset.navAction;
      const fallbackLink = element.dataset.linkUrl || '';
      if (!action) return;

      if (action === 'home_link' || action === 'custom_link') {
        this.activateHomeIcon(element);
        return;
      }

      event.preventDefault();
      this.activateHomeIcon(element);

      switch (action) {
        case 'search_drawer':
          window.dispatchEvent(new CustomEvent('search:open-drawer'));
          break;
        case 'cart_drawer':
          window.dispatchEvent(new CustomEvent('cart:open'));
          break;
        case 'wishlist_drawer': {
          const productId = this.getWishlistProductId();
          const inWishlistBefore = productId ? this.isProductInWishlist(productId) : null;

          let toggledViaApi = false;
          let toggledViaProxyBtn = false;
          let toggleResult = null;

          if (this.mode === 'product' && productId) {
            const wishlistPayload = this.buildWishlistPayload(productId);
            if (wishlistPayload && window.Wishlist && typeof window.Wishlist.toggle === 'function') {
              try {
                toggleResult = window.Wishlist.toggle(wishlistPayload);
                toggledViaApi = true;
              } catch (error) {
                // noop
              }
            }

            if (!toggledViaApi) {
              const nativeWishlistBtn = this.getNativeWishlistButton(productId);
              if (nativeWishlistBtn && typeof nativeWishlistBtn.click === 'function') {
                nativeWishlistBtn.click();
                toggledViaProxyBtn = true;
              }
            }

            this.syncWishlistState();
          }

          window.dispatchEvent(new CustomEvent('wishlist:open'));
          break;
        }
        case 'menu_drawer':
          if (!this.clickFirst('[data-drawer-toggle]') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'localization_drawer':
          if (!this.clickFirst('[data-localization-toggle]') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'collection_filter':
          if (!this.clickFirst('#filter-toggle') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'collection_sort':
          if (!this.openCollectionSort() && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        default:
          if (fallbackLink) {
            this.navigateTo(fallbackLink);
          }
      }
    }

    handleProductAction(event, element) {
      event.preventDefault();

      const action = element.dataset.productAction;
      if (action === 'add_to_bag') {
        this.handleAddToBag(element);
      } else if (action === 'buy_now') {
        this.handleBuyNow(element);
      }
    }

    bindWishlistState() {
      if (this.mode !== 'product' || !this.productWishlistBtn) return;
      document.addEventListener('wishlist:updated', this.handleWishlistUpdated);
      window.addEventListener('storage', this.handleWishlistUpdated);
      this.syncWishlistState();
    }

    handleWishlistUpdated(event) {
      if (event && event.type === 'storage' && event.key && event.key !== 'untamed_wishlist') {
        return;
      }
      this.syncWishlistState();
    }

    syncWishlistState() {
      if (!this.productWishlistBtn) return;
      const productId = this.getWishlistProductId();
      if (!productId) {
        this.productWishlistBtn.classList.remove('is-in-wishlist');
        return;
      }
      const inWishlist = this.isProductInWishlist(productId);
      this.productWishlistBtn.classList.toggle('is-in-wishlist', inWishlist);
    }

    getWishlistProductId() {
      if (!this.productWishlistBtn) return null;
      const productId = parseInt(this.productWishlistBtn.dataset.productId || '', 10);
      return Number.isFinite(productId) && productId > 0 ? productId : null;
    }

    isProductInWishlist(productId) {
      if (!productId) return false;

      if (window.Wishlist && typeof window.Wishlist.has === 'function') {
        try {
          return Boolean(window.Wishlist.has(productId));
        } catch (error) {
          // Fall through to localStorage fallback.
        }
      }

      try {
        const raw = window.localStorage ? window.localStorage.getItem('untamed_wishlist') : null;
        if (!raw) return false;
        const items = JSON.parse(raw);
        if (!Array.isArray(items)) return false;
        return items.some((item) => parseInt(item && item.id, 10) === productId);
      } catch (error) {
        return false;
      }
    }

    getNativeWishlistButton(productId) {
      if (!productId) return null;
      const exactBtn = document.querySelector(`[data-wishlist-btn][data-product-id="${productId}"]`);
      if (exactBtn) return exactBtn;
      const anyBtn = document.querySelector('[data-wishlist-btn]');
      return anyBtn || null;
    }

    getProductHandleFromPath() {
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (!match || !match[1]) return '';
      try {
        return decodeURIComponent(match[1]);
      } catch (error) {
        return match[1];
      }
    }

    buildWishlistPayload(productId) {
      if (!productId) return null;

      const sourceBtn = this.getNativeWishlistButton(productId);
      const variantId = this.getCurrentVariantId();

      const parseCents = (raw) => {
        const numeric = parseInt(raw || '', 10);
        return Number.isFinite(numeric) ? numeric : 0;
      };

      const handle = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productHandle) || this.getProductHandleFromPath();
      const title = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productTitle) ||
        (document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : document.title || '');
      const image = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productImage) ||
        (document.querySelector('meta[property="og:image"]') ? document.querySelector('meta[property="og:image"]').getAttribute('content') : '');
      const priceFromDataset = sourceBtn && sourceBtn.dataset ? sourceBtn.dataset.productPrice : '';
      const compareFromDataset = sourceBtn && sourceBtn.dataset ? sourceBtn.dataset.productCompareAtPrice : '';
      const price = parseCents(priceFromDataset);
      const compareAtPriceParsed = parseCents(compareFromDataset);
      const compareAtPrice = compareAtPriceParsed > 0 ? compareAtPriceParsed : null;
      const url = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productUrl) || window.location.pathname;

      if (!handle) return null;

      return {
        id: productId,
        handle: handle,
        title: title,
        image: image,
        price: price,
        compareAtPrice: compareAtPrice,
        url: url,
        variantId: (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.variantId) || variantId || null
      };
    }

    getFixedHeaderOffset() {
      const raw = window.getComputedStyle(document.documentElement).getPropertyValue('--untamed-fixed-header-offset').trim();
      const parsed = parseInt(raw || '0', 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    activateHomeIcon(element) {
      return element;
    }

    initCollectionPagination() {
      if (this.mode !== 'collection' || !this.collectionRoot) {
        return;
      }

      if (!this.enablePagination || !this.paginationWrap || !this.paginationPages) {
        this.setCollectionPaginationState(false);
        if (this.paginationWrap) this.paginationWrap.hidden = true;
        return;
      }

      const sourceNav = document.querySelector('.collection-pagination');
      if (!sourceNav) {
        this.paginationWrap.hidden = true;
        this.setCollectionPaginationState(false);
        return;
      }

      const prevLink = sourceNav.querySelector('a[aria-label="Previous page"]');
      const nextLink = sourceNav.querySelector('a[aria-label="Next page"], .collection-pagination__item--next');
      const pageParts = Array.from(sourceNav.querySelectorAll('.collection-pagination__item'))
        .filter((node) => /^\d+$/.test((node.textContent || '').trim()));

      this.paginationPages.innerHTML = '';
      if (!pageParts.length) {
        this.paginationWrap.hidden = true;
        this.setCollectionPaginationState(false);
        return;
      }

      pageParts.forEach((part) => {
        const title = (part.textContent || '').trim();
        const isActive = part.classList.contains('is-active') || part.getAttribute('aria-current') === 'page';
        const isLink = part.tagName.toLowerCase() === 'a';
        const href = isLink ? part.getAttribute('href') : '';

        const tagName = isLink ? 'a' : 'span';
        const pill = document.createElement(tagName);
        pill.className = 'floating-navbar__pagination-pill';
        if (isActive) pill.classList.add('is-active');
        pill.textContent = title;

        if (isLink && href) {
          pill.setAttribute('href', href);
          pill.dataset.paginationUrl = href;
        } else {
          pill.setAttribute('aria-current', 'page');
        }

        this.paginationPages.appendChild(pill);
      });

      if (this.paginationPrevBtn) {
        if (prevLink && prevLink.getAttribute('href')) {
          this.paginationPrevBtn.dataset.href = prevLink.getAttribute('href');
          this.paginationPrevBtn.disabled = false;
        } else {
          this.paginationPrevBtn.dataset.href = '';
          this.paginationPrevBtn.disabled = true;
        }
      }

      if (this.paginationNextBtn) {
        if (nextLink && nextLink.getAttribute('href')) {
          this.paginationNextBtn.dataset.href = nextLink.getAttribute('href');
          this.paginationNextBtn.disabled = false;
        } else {
          this.paginationNextBtn.dataset.href = '';
          this.paginationNextBtn.disabled = true;
        }
      }

      this.paginationWrap.hidden = false;
      this.setCollectionPaginationState(true);
      this.initIcons();
    }

    setCollectionPaginationState(active) {
      if (!this.collectionRoot) return;
      this.collectionRoot.classList.toggle('is-pagination-active', Boolean(active));
    }

    openCollectionSort() {
      const sortWrapper = this.getCollectionSortWrapper();
      if (!sortWrapper) {
        return false;
      }

      if (!sortWrapper._selectComponent) {
        if (typeof window.SelectComponent !== 'function') {
          return false;
        }
        sortWrapper._selectComponent = new window.SelectComponent(sortWrapper);
      }

      if (sortWrapper._selectComponent && typeof sortWrapper._selectComponent.open === 'function') {
        const fixedHeaderOffset = this.getFixedHeaderOffset();
        const wrapperRectBefore = sortWrapper.getBoundingClientRect();
        const needsScroll = wrapperRectBefore.top < fixedHeaderOffset || wrapperRectBefore.bottom > window.innerHeight;
        if (needsScroll) {
          const targetY = Math.max(0, window.scrollY + wrapperRectBefore.top - fixedHeaderOffset - 12);
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }

        requestAnimationFrame(() => {
          if (sortWrapper._selectComponent && typeof sortWrapper._selectComponent.open === 'function') {
            sortWrapper._selectComponent.open();
          }
        });
        return true;
      }

      return false;
    }

    getCollectionSortWrapper() {
      const wrapperCandidates = Array.from(document.querySelectorAll('.efs-toolbar__sort [data-select]'));
      const visibleWrapper = wrapperCandidates.find((el) => this.isElementVisible(el));
      if (visibleWrapper) return visibleWrapper;
      if (wrapperCandidates.length) return wrapperCandidates[0];

      const nativeCandidates = Array.from(document.querySelectorAll('#sort-select'));
      const visibleNative = nativeCandidates.find((el) => this.isElementVisible(el)) || nativeCandidates[0];
      return visibleNative ? visibleNative.closest('[data-select]') : null;
    }

    isElementVisible(element) {
      if (!element) return false;
      if (element.hidden) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return element.offsetParent !== null || style.position === 'fixed';
    }

    clickFirst(selector) {
      const el = document.querySelector(selector);
      if (el && typeof el.click === 'function') {
        el.click();
        return true;
      }
      return false;
    }

    navigateTo(url) {
      if (!url || url === '#') return;
      window.location.href = url;
    }

    async handleAddToBag(button) {
      if (!button || button.disabled) return;

      this.setActionLoading(button, true);
      try {
        const mainProductForm = document.querySelector('[data-product-form]') || document.querySelector('form[action="/cart/add"]');
        if (mainProductForm) {
          if (typeof mainProductForm.requestSubmit === 'function') {
            mainProductForm.requestSubmit();
          } else {
            mainProductForm.submit();
          }
          this.setActionLoading(button, false, 900);
          return;
        }

        await this.addCurrentVariantViaApi();
        this.setActionLoading(button, false, 600);
      } catch (error) {
        console.error('Floating navbar add-to-bag failed:', error);
        this.setActionLoading(button, false);
      }
    }

    async handleBuyNow(button) {
      if (!button || button.disabled) return;

      const dynamicCheckoutBtn = document.querySelector(
        '.product-add-to-cart-block__dynamic-checkout .shopify-payment-button__button, .shopify-payment-button__button'
      );
      if (dynamicCheckoutBtn && !dynamicCheckoutBtn.disabled) {
        dynamicCheckoutBtn.click();
        return;
      }

      this.setActionLoading(button, true);
      try {
        await this.addCurrentVariantViaApi();
        window.location.href = '/checkout';
      } catch (error) {
        console.error('Floating navbar buy-now failed:', error);
        this.setActionLoading(button, false);
      }
    }

    async addCurrentVariantViaApi() {
      const variantId = this.getCurrentVariantId();
      if (!variantId) {
        throw new Error('No variant ID available for add to cart.');
      }

      const parsedId = parseInt(variantId, 10);
      if (window.CartAPI && Number.isFinite(parsedId)) {
        await window.CartAPI.addItem(parsedId, 1);
        return;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parsedId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add current variant to cart.');
      }

      const item = await response.json();
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      window.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item: item, cart: cart }
      }));
      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));
    }

    getCurrentVariantId() {
      const form = document.querySelector('[data-product-form]') || document.querySelector('form[action="/cart/add"]');
      if (form) {
        const formVariantField = form.querySelector('[name="id"]');
        if (formVariantField && formVariantField.value) return formVariantField.value;
      }

      const checkedVariant = document.querySelector('input[name="id"]:checked');
      if (checkedVariant && checkedVariant.value) return checkedVariant.value;

      const selectedVariant = document.querySelector('select[name="id"]');
      if (selectedVariant && selectedVariant.value) return selectedVariant.value;

      return null;
    }

    setActionLoading(button, loading, releaseDelay) {
      if (!button) return;
      if (loading) {
        button.disabled = true;
        button.classList.add('is-loading');
        return;
      }

      const clear = () => {
        button.disabled = false;
        button.classList.remove('is-loading');
      };

      if (releaseDelay && releaseDelay > 0) {
        window.setTimeout(clear, releaseDelay);
      } else {
        clear();
      }
    }

  }

  const manager = {
    instances: new Map(),

    initIn(container) {
      const scope = container && container.querySelectorAll ? container : document;
      const roots = scope.querySelectorAll('[data-floating-navbar]');

      roots.forEach((root) => {
        const sectionId = root.dataset.sectionId || root.id;
        if (!sectionId || this.instances.has(sectionId)) return;
        const instance = new FloatingNavbarInstance(root);
        instance.init();
        this.instances.set(sectionId, instance);
      });
    },

    destroyIn(container) {
      if (!container || !container.querySelectorAll) return;
      const roots = container.querySelectorAll('[data-floating-navbar]');
      roots.forEach((root) => {
        const sectionId = root.dataset.sectionId || root.id;
        const instance = this.instances.get(sectionId);
        if (!instance) return;
        instance.destroy();
        this.instances.delete(sectionId);
      });
    },

    destroyAll() {
      this.instances.forEach((instance) => instance.destroy());
      this.instances.clear();
    }
  };

  window.UntamedFloatingNavbarManager = manager;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      manager.initIn(document);
    });
  } else {
    manager.initIn(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    manager.initIn(event.target);
  });

  document.addEventListener('shopify:section:unload', function(event) {
    manager.destroyIn(event.target);
  });
})();
